import { useQuery } from '@tanstack/react-query';
import { ApiConfig } from './types';
import { useMemo } from 'react';

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
}

export interface ApiError {
  message: string;
  status?: number;
  statusText?: string;
}

/**
 * Fetches data from an API endpoint using React Query
 */
export async function fetchApiData(
  apiConfig: ApiConfig,
  baseUrl?: string,
  defaultHeaders?: Record<string, string>,
  dependentValues?: Record<string, any> // Changed from formData to dependentValues
): Promise<Array<{ label: string; value: string | number }>> {
  try {
    const {
      endpoint,
      method = 'GET',
      valueField = 'value',
      labelField = 'label',
      headers = {},
      params = {},
      body,
      transform,
      dependsOn,
    } = apiConfig;

    // Build the full URL
    let url = endpoint;
    if (baseUrl && !endpoint.startsWith('http')) {
      url = `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    }

    // Replace dynamic parameters in the URL
    if (dependsOn && dependentValues) {
      const dependentValue = dependentValues[dependsOn];
      if (dependentValue) {
        url = url.replace(`{${dependsOn}}`, encodeURIComponent(dependentValue));
      }
    }

    // Add query parameters
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    if (queryParams.toString()) {
      url += `${url.includes('?') ? '&' : '?'}${queryParams.toString()}`;
    }

    // Prepare headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...defaultHeaders,
      ...headers,
    };

    // Prepare request options
    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders,
    };

    // Add body for POST/PUT requests
    if ((method === 'POST' || method === 'PUT') && body) {
      requestOptions.body = JSON.stringify(body);
    }

    // Make the API call
    const response = await fetch(url, requestOptions);

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Transform the data if a custom transform function is provided
    if (transform) {
      return transform(Array.isArray(data) ? data : data.data || data);
    }

    // Default transformation
    const items = Array.isArray(data) ? data : data.data || data;
    
    if (!Array.isArray(items)) {
      throw new Error('API response is not an array');
    }

    return items.map((item: any) => ({
      label: item[labelField] || item.name || item.title || String(item[valueField] || ''),
      value: item[valueField] || item.id || item.value || '',
    }));

  } catch (error) {
    console.error('API fetch error:', error);
    throw new Error(`Failed to fetch data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * React Query hook for fetching API data with caching - only fetches on user interaction
 */
export function useApiData(
  apiConfig: ApiConfig | null,
  baseUrl?: string,
  defaultHeaders?: Record<string, string>,
  dependentValues?: Record<string, any>, // Changed from formData to dependentValues
  enabled: boolean = false // Default to false - only fetch on user interaction
) {
  // Create a stable query key for dependent values
  const queryKeyDependentValues = useMemo(() => {
    if (!apiConfig?.dependsOn || !dependentValues) return null;
    
    const dependentValue = dependentValues[apiConfig.dependsOn];
    return dependentValue !== undefined ? dependentValue : null;
  }, [apiConfig?.dependsOn, dependentValues]);

  // Create a stable query key that only changes when necessary
  const queryKey = useMemo(() => [
    'api-data',
    apiConfig?.endpoint,
    apiConfig?.method,
    apiConfig?.valueField,
    apiConfig?.labelField,
    baseUrl,
    queryKeyDependentValues, // Only include the actual dependent value, not an object
  ], [apiConfig?.endpoint, apiConfig?.method, apiConfig?.valueField, apiConfig?.labelField, baseUrl, queryKeyDependentValues]);

  return useQuery({
    queryKey,
    queryFn: () => {
      if (!apiConfig) {
        throw new Error('API config is required');
      }
      return fetchApiData(apiConfig, baseUrl, defaultHeaders, dependentValues);
    },
    enabled: enabled && !!apiConfig, // Only fetch when explicitly enabled (user interaction)
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: false, // Don't refetch on mount if we already have data
  });
}

/**
 * Checks if a field has API configuration
 */
export function hasApiConfig(field: any): boolean {
  return !!(field.apiConfig || field.api_endpoint);
}

/**
 * Gets the API configuration from a field
 */
export function getApiConfig(field: any): ApiConfig | null {
  if (field.apiConfig) {
    return field.apiConfig;
  }

  if (field.api_endpoint) {
    return {
      endpoint: field.api_endpoint,
      method: field.api_method || 'GET',
      valueField: field.value_field || 'value',
      labelField: field.label_field || 'label',
      dependsOn: field.depends_on,
      conditional: field.conditional,
    };
  }

  return null;
}

/**
 * Checks if a field should be shown based on conditional logic
 */
export function shouldShowField(field: any, formData: Record<string, any>): boolean {
  const apiConfig = getApiConfig(field);
  
  // If field is not conditional, always show it
  if (!apiConfig?.conditional && !field.conditional) {
    return true;
  }

  // If field depends on another field, check if the dependent field has a value
  if (apiConfig?.dependsOn || field.depends_on) {
    const dependsOn = apiConfig?.dependsOn || field.depends_on;
    if (dependsOn) {
      const dependentValue = formData[dependsOn];
      return dependentValue !== undefined && dependentValue !== null && dependentValue !== '';
    }
  }

  return true;
} 